import AdminPage from '../modules/apps/admin/AdminPage'
import { Permission } from '../../_metronic/helpers'
import AccountPage from '../modules/accounts/AccountPage'
import ApiPage from '../modules/apps/api/ApiPage'
import AtendimentosAsoPage from '../modules/apps/atendimento-ASO/AtendimentoAsoPage'
import DownloadAsoPage from '../modules/apps/atendimento-ASO/DownloadAsoPage'
import AtendimentosPage from '../modules/apps/atendimento/AtendimentosPage'
import AtendimentosMedicosPage from '../modules/apps/atendimento_medicos/AtendimentoMedicoPage'
import AvaliacoesPage from '../modules/apps/avaliacoes/AvaliacoesPage'
import EmpresasPage from '../modules/apps/empresa/EmpresaPage'
import EmpresasClientePage from '../modules/apps/empresa/EmpresaPageCliente'
import EnderecosPage from '../modules/apps/enderecos/EnderecosPage'
import EscalasPage from '../modules/apps/escalas/EscalasPage'
import EspecialidadePage from '../modules/apps/especialidades/EspecialidadesPage'
import EsusPage from '../modules/apps/esus/EsusPage'
import FinanceiroPage from '../modules/apps/financeiro/FinanceiroPage'
import FormulariosPage from '../modules/apps/formularios/FormulariosPage'
import { FuncionariosPage } from '../modules/apps/funcionarios/FuncionariosPage'
import ImportacaoPage from '../modules/apps/importacao/ImportacaoPage'
import IndicadoresPage from '../modules/apps/indicadores/IndicadoresPage'
import InterconsultaRelatoriosPage from '../modules/apps/interconsulta-relatorios/InterconsultaRelatoriosPage'
import PersonalAtendimentosPage from '../modules/apps/meus-atendimentos/PersonalAtendimentosPage'
import MonitoramentoPage from '../modules/apps/monitoramento/MonitoramentoPage'
import PacientesPage from '../modules/apps/paciente-management/PacientesPage'
import PareceresPage from '../modules/apps/pareceres/PareceresPage'
import ProtocolosTriagemPage from '../modules/apps/protocolos-triagem/ProtocolosTriagemPage'
import NotificacoesPage from '../modules/apps/notificacoes/NotificacoesPage'
import NotificacoesEnviadasPage from '../modules/apps/notificacoes/NotificacoesEnviadasPage'
import NovidadesPage from '../modules/apps/notas-versao/NovidadesPage'
import { PegaPlantaoPage } from '../modules/apps/pega-plantao/PegaPlantaoPage'
import RelatorioNpsPage from '../modules/apps/relatorio-nps/RelatorioNpsPage'
import RelatorioPage from '../modules/apps/relatorios/RelatorioPage'
import SncrPage from '../modules/apps/sncr/SncrPage'
import { UnidadesPage } from '../modules/apps/unidades/UnidadesPage'
import { BrandManagementPage } from 'app/modules/brand-management'

export interface AppRoute {
  path: string
  element: JSX.Element
  allowedPermissions?: Permission[]
}

export const appRoutes: AppRoute[] = [
  {
    path: 'crafted/account/*',
    element: <AccountPage />,
  },
  {
    path: 'apps/notificacoes/*',
    element: <NotificacoesPage />,
  },
  {
    // Notas de versão: qualquer autenticado (o backend só exige isAuthenticated()).
    path: 'apps/novidades/*',
    element: <NovidadesPage />,
  },
  {
    // Mais específica que `apps/notificacoes/*`, então o react-router escolhe esta. Só SuperAdmin:
    // o backend também libera `Notificacao:read` a SUPER_GERENTE/SUPER_MEDICO, mas o papel ADMIN
    // herda `Permission.SuperGerente` no ROLE_PERMISSIONS e receberia 403.
    path: 'apps/notificacoes/enviadas',
    element: <NotificacoesEnviadasPage />,
    allowedPermissions: [Permission.SuperAdmin],
  },
  {
    path: 'apps/pacientes-management/*',
    element: <PacientesPage />,
    allowedPermissions: [
      Permission.Employee,
      Permission.SuperGerente,
      Permission.Medico,
      Permission.Solicitante,
    ],
  },
  {
    path: 'apps/atendimentosAso/*',
    element: <AtendimentosAsoPage />,
    allowedPermissions: [Permission.Employee, Permission.SuperGerente, Permission.Medico],
  },
  {
    path: 'apps/downloadAso/*',
    element: <DownloadAsoPage />,
    allowedPermissions: [Permission.Employee, Permission.SuperGerente, Permission.Medico],
  },
  {
    path: 'apps/relatorios/*',
    element: <RelatorioPage />,
    allowedPermissions: [Permission.Employee, Permission.SuperMedico, Permission.SuperGerente],
  },
  {
    path: 'apps/atendimentosMedicos/*',
    element: <AtendimentosMedicosPage />,
    allowedPermissions: [Permission.Employee, Permission.SuperGerente, Permission.Medico],
  },
  {
    path: 'apps/avaliacoes/*',
    element: <AvaliacoesPage />,
    allowedPermissions: [Permission.Employee, Permission.SuperGerente, Permission.Medico],
  },
  {
    path: 'apps/importacao/*',
    element: <ImportacaoPage />,
    allowedPermissions: [Permission.Employee, Permission.SuperGerente, Permission.Medico],
  },
  {
    // Acompanhamento dos envios ao PEC e-SUS APS (E5d). O par [SuperAdmin, Admin] libera
    // exatamente SUPER_ADMIN e ADMIN pelo `ROLE_PERMISSIONS` de `PrivateRoutes` — o mesmo conjunto
    // de grupos de `Esus:read`/`Esus:resend` no `permission.json`. Não copiar o
    // `allowedPermissions` de `apps/importacao/*`, que é bem mais largo.
    path: 'apps/esus/*',
    element: <EsusPage />,
    allowedPermissions: [Permission.SuperAdmin, Permission.Admin],
  },
  {
    // Numeração SNCR (Anvisa) do profissional. [Medico] resolve, pelo `ROLE_PERMISSIONS`, para
    // SUPER_ADMIN, SUPER_MEDICO e MEDICO. No backend não há perfil: os endpoints exigem usuário
    // autenticado com vínculo médico na empresa. Não usar `apps/atendimentosMedicos/*`: libera Employee e
    // SuperGerente, que veriam a tela e levariam 403.
    path: 'apps/sncr/*',
    element: <SncrPage />,
    allowedPermissions: [Permission.Medico],
  },
  {
    path: 'apps/meus-atendimentos/*',
    element: <PersonalAtendimentosPage />,
    allowedPermissions: [Permission.Employee, Permission.SuperGerente, Permission.Medico],
  },
  {
    path: 'apps/relatorio-nps/*',
    element: <RelatorioNpsPage />,
    allowedPermissions: [Permission.SuperAdmin, Permission.SuperMedico, Permission.Admin],
  },
  {
    // Mesma lista da tela irmã de NPS e exatamente os grupos para quem a autoridade
    // `Atendimento:indicadores` é semeada no backend (ADMIN, SUPER_MEDICO, SUPER_ADMIN).
    // Sem Permission.Usuario: paciente não acessa.
    path: 'apps/indicadores/*',
    element: <IndicadoresPage />,
    allowedPermissions: [Permission.SuperAdmin, Permission.SuperMedico, Permission.Admin],
  },
  {
    path: 'apps/relatorios-interconsulta/*',
    element: <InterconsultaRelatoriosPage />,
    allowedPermissions: [Permission.SuperAdmin, Permission.SuperMedico, Permission.Admin],
  },
  {
    path: 'apps/funcionarios/*',
    element: <FuncionariosPage />,
    allowedPermissions: [
      Permission.SuperAdmin,
      Permission.SuperMedico,
      Permission.Admin,
      Permission.SuperGerente,
    ],
  },
  {
    path: 'apps/empresas/*',
    element: <EmpresasPage />,
    allowedPermissions: [
      Permission.SuperAdmin,
      Permission.SuperMedico,
      Permission.Admin,
      Permission.SuperGerente,
    ],
  },
  {
    path: 'apps/api',
    element: <ApiPage />,
    allowedPermissions: [
      Permission.SuperAdmin,
      Permission.SuperMedico,
      Permission.Admin,
      Permission.SuperGerente,
    ],
  },
  {
    path: 'apps/especialidades',
    element: <EspecialidadePage />,
    allowedPermissions: [
      Permission.SuperAdmin,
      Permission.SuperMedico,
      Permission.Admin,
      Permission.SuperGerente,
    ],
  },
  {
    path: 'apps/formularios/*',
    element: <FormulariosPage />,
    allowedPermissions: [
      Permission.SuperAdmin,
      Permission.SuperMedico,
      Permission.Admin,
      Permission.SuperGerente,
    ],
  },
  {
    // A autoridade real é ProtocoloTriagem:read, conferida pelo backend; o front só conhece papéis,
    // então a rota filtra pelos papéis que recebem esse perfil e a tela converte 403 em /error/404.
    path: 'apps/protocolos-triagem/*',
    element: <ProtocolosTriagemPage />,
    allowedPermissions: [
      Permission.SuperAdmin,
      Permission.SuperMedico,
      Permission.Admin,
      Permission.Medico,
    ],
  },
  {
    path: 'apps/empresasCliente/*',
    element: <EmpresasClientePage />,
    allowedPermissions: [
      Permission.SuperAdmin,
      Permission.Admin,
      Permission.Employee,
      Permission.Medico,
    ],
  },
  {
    path: 'apps/atendimentos/*',
    element: <AtendimentosPage />,
    allowedPermissions: [Permission.Usuario],
  },
  {
    path: 'apps/unidades/*',
    element: <UnidadesPage />,
    allowedPermissions: [Permission.SuperAdmin, Permission.Admin],
  },
  {
    // Listagem de endereços de uma Instituição, Entidade ou Unidade/Setor. Aberta pelo menu
    // "Listar endereços" de cada uma dessas listas, por isso aceita as permissões das duas.
    path: 'apps/enderecos/*',
    element: <EnderecosPage />,
    allowedPermissions: [
      Permission.SuperAdmin,
      Permission.SuperMedico,
      Permission.Admin,
      Permission.SuperGerente,
    ],
  },
  {
    path: 'apps/pareceres/*',
    element: <PareceresPage />,
    allowedPermissions: [
      Permission.SuperAdmin,
      Permission.SuperMedico,
      Permission.Admin,
      Permission.Employee,
      Permission.Medico,
      Permission.Solicitante,
    ],
  },
  {
    path: 'apps/monitoramento/*',
    element: <MonitoramentoPage />,
    allowedPermissions: [Permission.SuperAdmin, Permission.SuperMedico, Permission.Admin],
  },
  {
    // Escala de atendimento (disponibilidade/indisponibilidade + feriados).
    // NÃO confundir com a rota `agenda/*` abaixo, que é o Pega Plantão.
    path: 'apps/escalas/*',
    element: <EscalasPage />,
    allowedPermissions: [
      Permission.SuperAdmin,
      Permission.SuperMedico,
      Permission.Admin,
      Permission.Medico,
      Permission.Employee,
    ],
  },
  {
    path: 'agenda/*',
    element: <PegaPlantaoPage />,
    allowedPermissions: [
      Permission.SuperAdmin,
      Permission.Admin,
      Permission.Employee,
      Permission.Medico,
    ],
  },
  {
    path: 'financeiro/*',
    element: <FinanceiroPage />,
    allowedPermissions: [
      Permission.SuperAdmin,
      Permission.SuperGerente,
      Permission.Admin,
      Permission.Employee,
      Permission.SuperMedico,
      Permission.Medico,
    ],
  },
  {
    path: 'apps/admin/*',
    element: <AdminPage />,
    allowedPermissions: [Permission.SuperAdmin],
  },
  {
    path: 'configuracoes/*',
    element: <BrandManagementPage />,
    allowedPermissions: [Permission.SuperAdmin, Permission.Admin],
  },
]
